import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import SidebarLayout from "./SidebarLayout";
import "./shopStyles.css";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "https://api.ogestyle.com";

const BillingPayouts = () => {
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [loading, setLoading] = useState(true);
  const [alert, setAlert] = useState({ show: false, message: "", type: "" });
  const [businessId, setBusinessId] = useState(null);
  const [shopId, setShopId] = useState(null);

  const [billingData, setBillingData] = useState({
    stripeAccountId: "",
    chargesEnabled: false,
    payoutsEnabled: false,
  });

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    fetchSettings();

    // Check if user just returned from Stripe onboarding
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get("stripe") === "success") {
      fetchSettings();
      showAlert(
        "Stripe account connected successfully! Your charges and payouts status has been updated.",
        "success",
      );
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (urlParams.get("stripe") === "refresh") {
      showAlert("Please complete your Stripe onboarding", "info");
      window.history.replaceState({}, document.title, window.location.pathname);
    }
    // eslint-disable-next-line
  }, []);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem("ogestyle_token");

      let busId = null;
      let shopIdValue = null;

      try {
        const staffRes = await axios.get(`${API_BASE_URL}/api/staff/profile`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const staffData = staffRes.data.staff || staffRes.data;

        if (staffData) {
          busId =
            typeof staffData.businessId === "object"
              ? staffData.businessId?._id
              : staffData.businessId;
          shopIdValue =
            typeof staffData.activeLocationId === "object"
              ? staffData.activeLocationId?._id
              : staffData.activeLocationId;
        }
      } catch (error) {
      }

      if (!busId || !shopIdValue) {
        const shopsRes = await axios.get(`${API_BASE_URL}/api/shop/my-shops`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const shops = Array.isArray(shopsRes.data)
          ? shopsRes.data
          : shopsRes.data.shops || [];

        if (shops.length > 0) {
          busId = busId || shops[0].businessId?._id || shops[0].businessId;
          shopIdValue = shopIdValue || shops[0]._id;
        }
      }

      if (!busId || !shopIdValue) {
        showAlert("No business associated with your account", "error");
        setLoading(false);
        return;
      }

      setBusinessId(busId);
      setShopId(shopIdValue);

      const businessRes = await axios.get(
        `${API_BASE_URL}/api/businesses/${busId}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );

      // If Stripe account exists, sync the latest status from Stripe
      if (businessRes.data.stripeAccountId) {
        try {
          const statusRes = await axios.get(
            `${API_BASE_URL}/api/stripe/account-status/${busId}`,
            { headers: { Authorization: `Bearer ${token}` } },
          );

          setBillingData({
            stripeAccountId: statusRes.data.stripeAccountId || "",
            chargesEnabled: statusRes.data.chargesEnabled || false,
            payoutsEnabled: statusRes.data.payoutsEnabled || false,
          });
        } catch (statusErr) {
          setBillingData({
            stripeAccountId: businessRes.data.stripeAccountId || "",
            chargesEnabled: businessRes.data.chargesEnabled || false,
            payoutsEnabled: businessRes.data.payoutsEnabled || false,
          });
        }
      } else {
        setBillingData({
          stripeAccountId: "",
          chargesEnabled: false,
          payoutsEnabled: false,
        });
      }

      setLoading(false);
    } catch (err) {
      showAlert("Failed to load settings", "error");
      setLoading(false);
    }
  };

  const showAlert = (message, type = "success") => {
    setAlert({ show: true, message, type });
    setTimeout(() => setAlert({ show: false, message: "", type: "" }), 5000);
  };

  const openStripeUrl = (url) => {
    if (!url) return false;
    try {
      const parsed = new URL(url);
      const isStripeHost =
        parsed.hostname === "stripe.com" ||
        parsed.hostname.endsWith(".stripe.com");
      if (!isStripeHost) {
        showAlert("Invalid Stripe redirect URL.", "error");
        return false;
      }
      window.open(url, "_blank", "noopener,noreferrer");
      return true;
    } catch (error) {
      showAlert("Invalid Stripe redirect URL.", "error");
      return false;
    }
  };

  const openStripeSettings = async () => {
    if (!shopId) {
      showAlert(
        "Business information still loading, please try again.",
        "error",
      );
      return;
    }

    try {
      const token = localStorage.getItem("ogestyle_token");
      const headers = { Authorization: `Bearer ${token}` };

      // Existing Stripe account: open Express dashboard login link first.
      if (billingData.stripeAccountId) {
        try {
          const loginRes = await axios.post(
            `${API_BASE_URL}/api/stripe/create-login-link/${shopId}`,
            {},
            { headers },
          );

          if (loginRes.data?.url) {
            openStripeUrl(loginRes.data.url);
            return;
          }
        } catch (loginErr) {
          // Fall through to onboarding link path when login link cannot be created.
        }
      }

      const response = await axios.post(
        `${API_BASE_URL}/api/stripe/connect-account`,
        { shopId },
        { headers },
      );

      if (response.data?.onboardingUrl) {
        openStripeUrl(response.data.onboardingUrl);
        return;
      }

      showAlert("Unable to open Stripe settings right now.", "error");
    } catch (err) {
      showAlert(
        err.response?.data?.message || "Failed to open Stripe settings",
        "error",
      );
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="bookings-container settings-page">
          <div className="loading-state">Loading settings...</div>
        </div>
      </SidebarLayout>
    );
  }

  return (
    <SidebarLayout>
      <div className="bookings-container settings-page">
        <button
          onClick={() => navigate("/dashboard/settings")}
          style={{
            background: "white",
            border: "1px solid #e5e7eb",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "var(--font-size-standard, 13.5px)",
            color: "#374151",
            cursor: "pointer",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
            transition: "all 0.2s",
            fontWeight: "500",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = "#f9fafb";
            e.currentTarget.style.borderColor = "#d1d5db";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "white";
            e.currentTarget.style.borderColor = "#e5e7eb";
          }}
        >
          <span>←</span>
          <span>Back to Settings</span>
        </button>

        {alert.show && (
          <div className={`alert alert-${alert.type}`}>{alert.message}</div>
        )}

        <div className="bookings-header">
          <div>
            <h1
              className="bookings-title"
              style={{ fontSize: "var(--font-size-title, 14.5px)" }}
            >
              Payment Settings
            </h1>
            <p
              className="bookings-subtitle"
              style={{ fontSize: "var(--font-size-standard, 13.5px)" }}
            >
              Manage Stripe billing and payout controls
            </p>
          </div>
        </div>

        <div className="settings-section">
          <div className="stripe-status-card">
            <div className="stripe-status-header">
              <h3>Stripe Account Status</h3>
              {billingData.stripeAccountId ? (
                <span className="status-badge active">Connected</span>
              ) : (
                <span className="status-badge inactive">Not Connected</span>
              )}
            </div>

            {billingData.stripeAccountId ? (
              <div className="stripe-info">
                <div className="info-row">
                  <span className="info-label">Account ID:</span>
                  <span className="info-value">
                    {"•••••••••" + billingData.stripeAccountId.slice(-6)}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Charges:</span>
                  <span
                    className={`info-value ${billingData.chargesEnabled ? "text-success" : "text-error"}`}
                  >
                    {billingData.chargesEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                <div className="info-row">
                  <span className="info-label">Payouts:</span>
                  <span
                    className={`info-value ${billingData.payoutsEnabled ? "text-success" : "text-error"}`}
                  >
                    {billingData.payoutsEnabled ? "Enabled" : "Disabled"}
                  </span>
                </div>
                {(!billingData.chargesEnabled ||
                  !billingData.payoutsEnabled) && (
                  <div
                    style={{
                      marginTop: "16px",
                      padding: "12px",
                      backgroundColor: "#fff3cd",
                      borderRadius: "8px",
                      fontSize: "var(--font-size-standard, 13.5px)",
                      color: "#856404",
                    }}
                  >
                    <strong>Note:</strong> Complete your Stripe onboarding to
                    enable charges and payouts.
                  </div>
                )}
              </div>
            ) : (
              <div className="stripe-connect-prompt">
                <p>
                  Connect your Stripe account to receive payments and payouts.
                </p>
                <button
                  className="btn btn-primary"
                  onClick={openStripeSettings}
                  style={{ width: isMobile ? "100%" : "auto" }}
                >
                  Connect Stripe Account
                </button>
              </div>
            )}
          </div>

          {billingData.stripeAccountId && (
            <div className="settings-actions" style={{ flexWrap: "wrap" }}>
              <button
                className="btn btn-secondary"
                onClick={openStripeSettings}
                style={{ width: isMobile ? "100%" : "auto" }}
              >
                Reconnect or Manage Stripe
              </button>
            </div>
          )}
        </div>

        {/* Bank Account Management Info */}
        {businessId && billingData.stripeAccountId && (
          <div style={{ marginTop: "32px" }}>
            <div
              style={{
                background: "#eff6ff",
                border: "1px solid #bfdbfe",
                borderRadius: "8px",
                padding: "20px",
                borderLeft: "4px solid #3b82f6",
              }}
            >
              <h3
                style={{
                  margin: "0 0 12px 0",
                  fontSize: "var(--font-size-title, 14.5px)",
                  fontWeight: "600",
                  color: "#1e40af",
                }}
              >
                💳 Bank Account Management
              </h3>
              <p
                style={{ margin: "8px 0", fontSize: "var(--font-size-standard, 13.5px)", color: "#1e40af" }}
              >
                Your business bank account for receiving payouts is managed
                securely through your Stripe account.
              </p>
              <p
                style={{ margin: "8px 0", fontSize: "var(--font-size-standard, 13.5px)", color: "#1e40af" }}
              >
                To update your bank account details, click the{" "}
                <strong>"Manage Stripe Account"</strong> button above to access
                your Stripe Express Dashboard where you can:
              </p>
              <ul
                style={{
                  margin: "12px 0 12px 20px",
                  fontSize: "var(--font-size-standard, 13.5px)",
                  color: "#1e40af",
                }}
              >
                <li>Add or update bank account information</li>
                <li>View payout schedule (Weekly on Fridays, 7-day hold)</li>
                <li>Update business details and tax information</li>
                <li>View payout history and account balance</li>
              </ul>
              <p
                style={{
                  margin: "12px 0 0 0",
                  fontSize: "12px",
                  color: "#6b7280",
                  fontStyle: "italic",
                }}
              >
                🔒 All bank account information is encrypted and stored securely
                by Stripe, never on our servers.
              </p>
            </div>
          </div>
        )}
      </div>
    </SidebarLayout>
  );
};

export default BillingPayouts;



